/* =============================================================================
   Joraleka — Cart Editor hero mock (v2)  ·  behaviour
   =============================================================================
   Vanilla, 0 dependency. Port of the validated React component's two effects:

     1. FIT — the stage is authored at a fixed design width (440px desktop,
        340px mobile) and uniformly scaled to the column width. Uniform scale
        is what keeps the drawer from being squashed or clipped at any
        breakpoint; the scaled height is written back onto .cem2 so the hero
        grid reserves the right room. Never set a fixed height in CSS.

     2. CYCLE — an animated cursor walks the editor card and, on each "click",
        toggles a block or switches the accent colour. The drawer reacts live
        and the changed element pulses (.cem-flash) so cause and effect read
        as one gesture.

   DELTA vs the drop-in file received: refit() and syncHeight() now bail out
   when nothing changed. The validated React component had that guard (it
   compared s / compact / h before setFit); the port had dropped it, which
   feeds the ResizeObserver a notification for every height write. Verified
   behaviour-identical across a 360→1440px sweep.

   Honours prefers-reduced-motion: the cursor and the cycle are skipped and
   the mock stays in its default state (all blocks on, green accent).
   ============================================================================= */
(function () {
  'use strict';

  var ACCENTS = ['#67a86b', '#c9784a', '#0e0f0a'];
  var W_FULL = 440, W_COMPACT = 340, COMPACT_AT = 700;
  var STEP_MS = 3100, FIRST_MS = 1400, DOWN_MS = 950, UP_MS = 1250, FLASH_MS = 1000;

  function init(root) {
    var stage = root.querySelector('.cem-stage');
    if (!stage) return;

    var el = {
      upsell: root.querySelector('.cem-upsell'),
      pay: root.querySelector('.cem-pay'),
      dhead: root.querySelector('.cem-dhead'),
      cta: root.querySelector('.cem-cta'),
      acct: root.querySelector('.cem-acct'),
      cursor: root.querySelector('.cem-cursor'),
      chipUpsell: root.querySelector('[data-cem-chip="upsell"]'),
      chipPay: root.querySelector('[data-cem-chip="pay"]'),
      chipSignin: root.querySelector('[data-cem-chip="signin"]'),
      swatches: root.querySelectorAll('[data-cem-accent]')
    };

    var scale = 1, lastCompact = null, lastH = -1;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------------- 1. fit ---------------- */
    /* The guard mirrors the validated React component (it compared s / compact /
       h before calling setFit). Without it every height we write back feeds the
       ResizeObserver a fresh notification, which is how a mock like this ends up
       emitting "ResizeObserver loop completed with undelivered notifications". */
    function syncHeight() {
      var h = stage.offsetHeight;
      if (Math.abs(h - lastH) <= 1) return;
      lastH = h;
      root.style.height = Math.round(h * scale) + 'px';
    }
    function refit() {
      var w = root.clientWidth;
      if (!w) return;
      var compact = (window.innerWidth || w) < COMPACT_AT;
      var dw = compact ? W_COMPACT : W_FULL;
      var s = w / dw;
      var changed = (Math.abs(s - scale) > 0.005) || (compact !== lastCompact);
      if (changed) {
        scale = s;
        lastCompact = compact;
        lastH = -1; /* force the height to be re-published at the new scale */
        stage.classList.toggle('compact', compact);
        stage.style.width = dw + 'px';
        stage.style.transform = 'scale(' + s + ')';
        root.setAttribute('data-cem-ready', '1');
      }
      syncHeight();
    }

    refit();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(refit);
      ro.observe(root);
      ro.observe(stage);
    } else {
      window.addEventListener('resize', refit);
    }

    /* ---------------- 2. state ---------------- */
    function setBlock(node, chip, on) {
      if (node) node.classList.toggle('off', !on);
      if (chip) chip.classList.toggle('on', on);
      setTimeout(syncHeight, 500); /* after the max-height transition settles */
    }
    function setAccent(i) {
      root.style.setProperty('--cem-acc', ACCENTS[i]);
      for (var n = 0; n < el.swatches.length; n++) {
        el.swatches[n].classList.toggle('on', n === i);
      }
    }
    var flashTimer;
    function flash(node) {
      if (!node) return;
      node.classList.remove('cem-flash');
      void node.offsetWidth; /* restart the keyframes */
      node.classList.add('cem-flash');
      clearTimeout(flashTimer);
      flashTimer = setTimeout(function () { node.classList.remove('cem-flash'); }, FLASH_MS);
    }

    if (reduce) return; /* static, fully-on state */

    /* ---------------- 3. cycle ---------------- */
    var steps = [
      { target: el.swatches[1], act: function () { setAccent(1); flash(el.cta); } },
      { target: el.chipUpsell,  act: function () { setBlock(el.upsell, el.chipUpsell, false); flash(el.upsell); } },
      { target: el.chipUpsell,  act: function () { setBlock(el.upsell, el.chipUpsell, true);  flash(el.upsell); } },
      { target: el.swatches[2], act: function () { setAccent(2); flash(el.cta); } },
      { target: el.chipPay,     act: function () { setBlock(el.pay, el.chipPay, false); flash(el.pay); } },
      { target: el.chipPay,     act: function () { setBlock(el.pay, el.chipPay, true);  flash(el.pay); } },
      { target: el.chipSignin,  act: function () { setBlock(el.acct, el.chipSignin, false); flash(el.dhead); } },
      { target: el.chipSignin,  act: function () { setBlock(el.acct, el.chipSignin, true);  flash(el.dhead); } },
      { target: el.swatches[0], act: function () { setAccent(0); flash(el.cta); } }
    ];

    var i = 0, tDown, tUp;
    function tick() {
      var step = steps[i % steps.length];
      i++;
      if (!step || !step.target) return;

      /* stage-space coordinates: undo the scale */
      var sr = stage.getBoundingClientRect();
      var tr = step.target.getBoundingClientRect();
      var x = (tr.left - sr.left) / scale + (tr.width / scale) / 2;
      var y = (tr.top - sr.top) / scale + (tr.height / scale) / 2;

      el.cursor.style.display = '';
      el.cursor.style.left = (x - 3) + 'px';
      el.cursor.style.top = (y - 2) + 'px';

      clearTimeout(tDown); clearTimeout(tUp);
      tDown = setTimeout(function () { el.cursor.classList.add('down'); step.act(); }, DOWN_MS);
      tUp = setTimeout(function () { el.cursor.classList.remove('down'); }, UP_MS);
    }

    setTimeout(tick, FIRST_MS);
    setInterval(tick, STEP_MS);
  }

  function boot() {
    var mocks = document.querySelectorAll('[data-cem-mock]');
    for (var n = 0; n < mocks.length; n++) init(mocks[n]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
